/**
 * Tipos para o sistema de busca avançada
 */

import { FileItem } from '../../../services/api'
import {
  TranscriptSegment,
  Entity,
  EntityType,
  SentimentLabel
} from '../../../types/transcription'

// ═══════════════════════════════════════════════════════════════
// QUERY TYPES
// ═══════════════════════════════════════════════════════════════

export interface SearchQuery {
  /** Texto da busca */
  query: string

  /** Operador para múltiplos termos */
  operator: 'AND' | 'OR'

  /** Tipos de conteúdo a buscar */
  searchIn: SearchScope

  /** Filtros estruturados */
  filters: SearchFilters

  /** Paginação */
  page: number
  pageSize: number

  /** Ordenação */
  sortBy: SearchSortField
  sortOrder: 'asc' | 'desc'
}

export interface SearchScope {
  /** Texto das transcrições */
  transcriptions: boolean
  /** Entidades extraídas (pessoas, locais, etc.) */
  entities: boolean
  /** Resumos gerados por IA */
  summaries: boolean
  /** Anotações do usuário */
  annotations: boolean
  /** Nomes de arquivos */
  filenames: boolean
}

export interface SearchFilters {
  /** Filtrar por falantes específicos */
  speakers?: string[]
  /** Filtrar por sentimento */
  sentiment?: SentimentLabel[]
  /** Filtrar por pastas */
  folders?: string[]
  /** Filtrar por arquivos específicos */
  fileIds?: string[]
  /** Filtrar por tipos de entidade */
  entityTypes?: EntityType[]
  /** Confiança mínima da transcrição (0-1) */
  minConfidence?: number
  /** Apenas segmentos com anotações */
  hasAnnotations?: boolean
  /** Apenas termos jurídicos */
  legalTermsOnly?: boolean
  /** Status do arquivo */
  fileStatus?: ('completed' | 'processing' | 'failed')[]
}

export type SearchSortField = 'relevance' | 'date' | 'speaker' | 'file'

// ═══════════════════════════════════════════════════════════════
// RESULT TYPES
// ═══════════════════════════════════════════════════════════════

export interface SearchResponse {
  /** Total de resultados encontrados */
  totalResults: number
  /** Página atual */
  page: number
  /** Tamanho da página */
  pageSize: number
  /** Tempo de execução em ms */
  queryTime: number

  /** Resultados agrupados por tipo */
  results: SearchResults

  /** Insights automáticos */
  insights: SearchInsights

  /** Sugestões de busca relacionadas */
  suggestions: string[]
}

export interface SearchResults {
  /** Matches em transcrições */
  transcriptMatches: TranscriptMatch[]
  /** Entidades encontradas */
  entityMatches: EntityMatch[]
  /** Arquivos relacionados */
  fileMatches: FileMatch[]
  /** Matches em resumos */
  summaryMatches: SummaryMatch[]
  /** Matches em anotações */
  annotationMatches: AnnotationMatch[]
}

export interface TranscriptMatch {
  /** ID do arquivo */
  fileId: string
  /** Nome do arquivo */
  fileName: string
  /** Índice do segmento */
  segmentIndex: number
  /** Falante/speaker */
  speaker: string
  /** Texto original */
  text: string
  /** Texto com highlights nos matches */
  highlightedText: string
  /** Timestamps */
  timestamp: {
    start: string
    end: string
  }
  /** Sentimento do trecho */
  sentiment?: SentimentLabel
  /** Confiança da transcrição */
  confidence: number
  /** Contexto ao redor do match */
  context: {
    before: string
    after: string
  }
  /** Score de relevância */
  relevanceScore: number
}

export interface EntityMatch {
  /** Dados da entidade */
  entity: {
    type: EntityType
    value: string
    normalizedValue?: string
  }
  /** ID do arquivo */
  fileId: string
  /** Nome do arquivo */
  fileName: string
  /** Número de ocorrências neste arquivo */
  occurrenceCount: number
  /** Lista de ocorrências */
  occurrences: {
    segmentIndex: number
    context: string
    timestamp: string
    speaker: string
  }[]
  /** Score de relevância */
  relevanceScore: number
}

export interface FileMatch {
  /** Dados do arquivo */
  file: FileItem
  /** Número de matches neste arquivo */
  matchCount: number
  /** Preview do primeiro match */
  preview?: string
  /** Preview com highlight HTML */
  highlightedPreview?: string
  /** Score de relevância */
  relevanceScore: number
}

export interface SummaryMatch {
  /** ID do arquivo */
  fileId: string
  /** Nome do arquivo */
  fileName: string
  /** Tipo de match (resumo, ponto-chave, etc.) */
  matchType: 'summary' | 'keyPoint' | 'actionItem' | 'decision'
  /** Texto com highlight */
  highlightedText: string
  /** Score de relevância */
  relevanceScore: number
}

export interface AnnotationMatch {
  /** ID do arquivo */
  fileId: string
  /** Nome do arquivo */
  fileName: string
  /** Tipo de anotação */
  annotationType: 'note' | 'bookmark' | 'highlight'
  /** Texto da anotação */
  text: string
  /** Texto com highlight */
  highlightedText: string
  /** Índice do segmento relacionado */
  segmentIndex?: number
  /** Timestamp */
  timestamp?: string
  /** Score de relevância */
  relevanceScore: number
}

// ═══════════════════════════════════════════════════════════════
// INSIGHTS TYPES
// ═══════════════════════════════════════════════════════════════

export interface SearchInsights {
  /** Possíveis contradições detectadas */
  contradictions: Contradiction[]
  /** Pessoas mais mencionadas nos resultados */
  topPeople: { name: string; count: number; fileCount: number }[]
  /** Locais mais mencionados */
  topLocations: { name: string; count: number }[]
  /** Timeline de eventos relacionados */
  eventTimeline: TimelineItem[]
  /** Distribuição de sentimento nos resultados */
  sentimentDistribution: {
    positive: number
    neutral: number
    negative: number
  }
}

export interface Contradiction {
  /** Tipo de contradição */
  type: 'time' | 'fact' | 'person' | 'location' | 'statement'
  /** Severidade */
  severity: 'low' | 'medium' | 'high'
  /** Primeira declaração */
  statementA: {
    fileId: string
    fileName: string
    segmentIndex: number
    speaker: string
    text: string
    timestamp: string
  }
  /** Segunda declaração (conflitante) */
  statementB: {
    fileId: string
    fileName: string
    segmentIndex: number
    speaker: string
    text: string
    timestamp: string
  }
  /** Análise da contradição */
  analysis: string
}

export interface TimelineItem {
  /** Timestamp ou data do evento */
  time: string
  /** Descrição do evento */
  description: string
  /** Arquivo de origem */
  fileId: string
  fileName: string
  /** Falante */
  speaker: string
  /** Índice do segmento */
  segmentIndex: number
}

// ═══════════════════════════════════════════════════════════════
// UI STATE TYPES
// ═══════════════════════════════════════════════════════════════

export interface SearchState {
  /** Query atual */
  query: string
  /** Filtros ativos */
  filters: SearchFilters
  /** Escopo da busca */
  scope: SearchScope
  /** Resultados */
  results: SearchResponse | null
  /** Estado de loading */
  isSearching: boolean
  /** Erro */
  error: string | null
  /** Histórico de buscas */
  searchHistory: string[]
  /** Categoria de resultado selecionada */
  activeCategory: SearchResultCategory
  /** Modal de busca aberto */
  isModalOpen: boolean
}

export type SearchResultCategory =
  | 'all'
  | 'transcripts'
  | 'entities'
  | 'files'
  | 'summaries'
  | 'annotations'

// ═══════════════════════════════════════════════════════════════
// HELPER TYPES
// ═══════════════════════════════════════════════════════════════

export interface ParsedSearchQuery {
  /** Termos de busca simples */
  terms: string[]
  /** Frases exatas (entre aspas) */
  phrases: string[]
  /** Filtros inline (ex: falante:"João") */
  inlineFilters: {
    field: string
    value: string
    operator: 'equals' | 'contains'
  }[]
  /** Termos negados (NOT) */
  excludedTerms: string[]
}

export interface SearchSuggestion {
  /** Texto da sugestão */
  text: string
  /** Tipo de sugestão */
  type: 'history' | 'entity' | 'file' | 'speaker' | 'term'
  /** Ícone sugerido */
  icon?: string
  /** Dados adicionais */
  metadata?: {
    fileId?: string
    entityType?: EntityType
    count?: number
  }
}

// ═══════════════════════════════════════════════════════════════
// DEFAULT VALUES
// ═══════════════════════════════════════════════════════════════

export const DEFAULT_SEARCH_SCOPE: SearchScope = {
  transcriptions: true,
  entities: true,
  summaries: true,
  annotations: true,
  filenames: true
}

export const DEFAULT_SEARCH_FILTERS: SearchFilters = {
  speakers: undefined,
  sentiment: undefined,
  folders: undefined,
  fileIds: undefined,
  entityTypes: undefined,
  minConfidence: undefined,
  hasAnnotations: undefined,
  legalTermsOnly: undefined,
  fileStatus: ['completed']
}

export const DEFAULT_SEARCH_QUERY: SearchQuery = {
  query: '',
  operator: 'AND',
  searchIn: DEFAULT_SEARCH_SCOPE,
  filters: DEFAULT_SEARCH_FILTERS,
  page: 1,
  pageSize: 20,
  sortBy: 'relevance',
  sortOrder: 'desc'
}
